import { useEffect, useRef } from "react";
import * as THREE from "three";

export default function LaserFlow({
  color = "#8FD6FF",
  horizontalBeamOffset = 0.04,
  verticalBeamOffset = 0.02,
  horizontalSizing = 0.42,
  verticalSizing = 1.65,
  wispDensity = 0.9,
  wispSpeed = 11.0,
  wispIntensity = 3.4,
  flowSpeed = 0.28,
  flowStrength = 0.2,
  fogIntensity = 0.34,
  fogScale = 0.28,
  fogFallSpeed = 0.5,
  decay = 1.25,
  falloffStart = 1.3,
  className = "h-full w-full",
}) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    mount.appendChild(renderer.domElement);

    const uniforms = {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uColor: { value: new THREE.Color(color) },
      uHorizontalBeamOffset: { value: horizontalBeamOffset },
      uVerticalBeamOffset: { value: verticalBeamOffset },
      uHorizontalSizing: { value: horizontalSizing },
      uVerticalSizing: { value: verticalSizing },
      uWispDensity: { value: wispDensity },
      uWispSpeed: { value: wispSpeed },
      uWispIntensity: { value: wispIntensity },
      uFlowSpeed: { value: flowSpeed },
      uFlowStrength: { value: flowStrength },
      uFogIntensity: { value: fogIntensity },
      uFogScale: { value: fogScale },
      uFogFallSpeed: { value: fogFallSpeed },
      uDecay: { value: decay },
      uFalloffStart: { value: falloffStart },
    };

    const material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;

        varying vec2 vUv;
        uniform vec2 uResolution;
        uniform vec3 uColor;
        uniform float uTime;
        uniform float uHorizontalBeamOffset;
        uniform float uVerticalBeamOffset;
        uniform float uHorizontalSizing;
        uniform float uVerticalSizing;
        uniform float uWispDensity;
        uniform float uWispSpeed;
        uniform float uWispIntensity;
        uniform float uFlowSpeed;
        uniform float uFlowStrength;
        uniform float uFogIntensity;
        uniform float uFogScale;
        uniform float uFogFallSpeed;
        uniform float uDecay;
        uniform float uFalloffStart;

        float hash(vec2 p) {
          p = fract(p * vec2(123.34, 456.21));
          p += dot(p, p + 34.345);
          return fract(p.x * p.y);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }

        float fbm(vec2 p) {
          float value = 0.0;
          float amp = 0.55;
          for (int i = 0; i < 5; i++) {
            value += amp * noise(p);
            p = p * 2.1 + vec2(9.3, 6.7);
            amp *= 0.52;
          }
          return value;
        }

        float softBeam(float axis, float thickness, float intensity) {
          return exp(-abs(axis) * thickness) * intensity;
        }

        void main() {
          vec2 uv = vUv;
          vec2 p = uv * 2.0 - 1.0;
          float ratio = uResolution.x / max(uResolution.y, 1.0);
          p.x *= ratio;

          float t = uTime;
          float drift = t * uFlowSpeed;

          float flowA = sin((p.y + uVerticalBeamOffset) * (6.5 * uVerticalSizing) + drift * 3.2);
          float flowB = sin((p.x + uHorizontalBeamOffset) * (7.5 * uHorizontalSizing) - drift * 2.8);
          float flowC = sin((p.y * 0.55 - p.x * 0.35) * 8.2 + drift * 2.0);
          float flow = (flowA + flowB + flowC) / 3.0;

          vec2 wispCoord = vec2(
            p.x * (1.45 + uWispDensity),
            p.y * (1.75 + uWispDensity) + t * (0.055 * uWispSpeed)
          );
          float wisp = fbm(wispCoord);
          float filaments = smoothstep(0.36, 0.85, wisp + flow * uFlowStrength);

          float beamX = softBeam(p.x + uHorizontalBeamOffset, 2.9 + uWispIntensity * 0.5, 0.9);
          float beamY = softBeam(p.y + uVerticalBeamOffset, 4.8 + uWispIntensity * 0.45, 0.65);
          float beamDiag = softBeam(p.x * 0.42 + p.y * 0.88 + uVerticalBeamOffset * 0.4, 5.8, 0.45);
          float beams = clamp(beamX + beamY + beamDiag, 0.0, 1.35);

          float fog = fbm(p * (3.2 * uFogScale) + vec2(0.0, -t * 0.17 * uFogFallSpeed));
          fog = smoothstep(0.2, 0.92, fog) * uFogIntensity;

          float vignette = smoothstep(uFalloffStart, 0.2, length(p));
          float energy = filaments * 0.9 + beams * 0.95 + fog * 0.55;
          energy = pow(clamp(energy, 0.0, 1.0), uDecay) * vignette;

          vec3 coreColor = uColor;
          vec3 accentColor = mix(uColor, vec3(0.76, 0.96, 1.0), 0.35);
          vec3 finalColor = mix(coreColor, accentColor, clamp(beams * 0.7 + fog * 0.35, 0.0, 1.0));
          finalColor *= (0.42 + energy * 1.42);

          float grain = (hash(gl_FragCoord.xy + t * 20.0) - 0.5) * 0.03;
          finalColor += grain;

          float alpha = clamp(energy * 0.72 + beams * 0.12, 0.0, 0.78);
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
    });

    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
    scene.add(quad);

    const clock = new THREE.Clock();
    const resize = () => {
      const width = mount.clientWidth || 1;
      const height = mount.clientHeight || 1;
      renderer.setSize(width, height, false);
      uniforms.uResolution.value.set(width, height);
    };

    resize();
    window.addEventListener("resize", resize);

    let rafId = 0;
    const render = () => {
      uniforms.uTime.value = clock.getElapsedTime();
      renderer.render(scene, camera);
      rafId = window.requestAnimationFrame(render);
    };
    render();

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      quad.geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [
    color,
    horizontalBeamOffset,
    verticalBeamOffset,
    horizontalSizing,
    verticalSizing,
    wispDensity,
    wispSpeed,
    wispIntensity,
    flowSpeed,
    flowStrength,
    fogIntensity,
    fogScale,
    fogFallSpeed,
    decay,
    falloffStart,
  ]);

  return <div ref={mountRef} className={className} />;
}
